import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, PhoneCall } from 'lucide-react';
import { AppContext } from './AppContext';
import './HeroBanner.css';

const HeroBanner = () => {
  const { t } = useContext(AppContext);
  const navigate = useNavigate();

  const [current, setCurrent] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const slides = [
    {
      id: 'necklace',
      image: '/images/hero-slide-necklace.png',
      tag: t.heroBannerTag1 || "✦ EXCLUSIVE WOMEN'S COLLECTION ✦",
      title1: t.heroBannerH1 || "Timeless Silver",
      title2: t.heroBannerH2 || "Necklaces & Pendants",
      sub: t.heroBannerSub1 || "Celebrate every special moment with handcrafted 925 sterling silver necklaces, statement pendants, and bridal neckwear made for timeless grace.",
      cta: t.heroBannerCta1 || "Explore Necklaces",
      link: '/products?category=For+Women&subcategory=Chains',
      enquirySubject: "Wholesale Enquiry - Women Silver Necklaces & Pendants"
    },
    {
      id: 'payal',
      image: '/images/hero-slide-payal.png',
      tag: t.heroBannerTag2 || "✦ MAHARASHTRA'S HERITAGE ARTISTRY ✦",
      title1: t.heroBannerH3 || "Heritage 925",
      title2: t.heroBannerH4 || "Silver Payal & Anklets",
      sub: t.heroBannerSub2 || "Discover our premier wholesale range of authentic silver payals, crafted with enduring shine, pure hallmark quality, and traditional motifs.",
      cta: t.heroBannerCta2 || "Explore Payal Collection",
      link: '/products?category=For+Women&subcategory=Payal',
      enquirySubject: "Wholesale Enquiry - 925 Silver Payal & Anklets"
    },
    {
      id: 'men-bracelet',
      image: '/images/hero-slide-men.png',
      tag: t.heroBannerTag3 || "✦ MEN'S LUXURY SIGNATURE ✦",
      title1: t.heroBannerH5 || "Men's Silver",
      title2: t.heroBannerH6 || "Bracelets & Kadas",
      sub: t.heroBannerSub3 || "Bold, distinguished, and built to last. Premium solid 925 sterling silver bracelets and designer kadas tailored for prestige and modern charisma.",
      cta: t.heroBannerCta3 || "Explore Men's Collection",
      link: '/products?category=For+Men&subcategory=Bracelets',
      enquirySubject: "Wholesale Enquiry - Men's Silver Bracelets & Kadas"
    }
  ];

  const totalSlides = slides.length;

  const nextSlide = useCallback(() => {
    setCurrent(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrent(prev => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = (idx) => {
    setCurrent(idx);
  };

  // Auto slide rotator (4s)
  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => {
      nextSlide();
    }, 4000);
    return () => clearInterval(timer);
  }, [isHovered, nextSlide]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) {
      nextSlide();
    } else if (distance < -50) {
      prevSlide();
    }
    setTouchStart(0);
    setTouchEnd(0);
  };

  return (
    <section
      className="hero-carousel-section"
      id="home"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      aria-label="Hero Carousel"
    >
      {/* Background Slides */}
      <div className="hero-slides-wrapper">
        {slides.map((slide, idx) => {
          const isActive = idx === current;
          return (
            <div
              key={slide.id}
              className={`hero-slide-item ${isActive ? 'active' : ''}`}
              style={{
                backgroundImage: `url('${slide.image}')`
              }}
            >
              {/* Soft gradient overlay so left text is 100% crisp while right side model remains visible */}
              <div className="hero-slide-gradient" />
            </div>
          );
        })}
      </div>

      {/* Slide Content Container (Left-Aligned) */}
      <div className="hero-content-container">
        {slides.map((slide, idx) => {
          const isActive = idx === current;
          if (!isActive) return null;

          return (
            <div key={slide.id} className="hero-text-card">
              <h1 className="hero-main-title">
                <span className="title-row-primary">{slide.title1}</span>
                <span className="title-row-accent">{slide.title2}</span>
              </h1>

              <div className="hero-title-divider">
                <span className="divider-line"></span>
                <span className="divider-gem">◆</span>
              </div>

              <p className="hero-description">{slide.sub}</p>

              <div className="hero-actions-row">
                <button
                  className="btn-hero-primary"
                  onClick={() => navigate(slide.link)}
                >
                  <span>{slide.cta}</span>
                  <ArrowRight size={17} className="btn-arrow" />
                </button>

                <button
                  className="btn-hero-secondary"
                  onClick={() => navigate(`/contact?subject=${encodeURIComponent(slide.enquirySubject)}`)}
                >
                  <PhoneCall size={15} />
                  <span>{t.enquireWholesale || "Enquire Wholesale"}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>



      {/* Bottom Controls / Indicators */}
      <div className="hero-dots-wrap">
        {slides.map((_, idx) => (
          <button
            key={idx}
            className={`hero-dot-pill ${idx === current ? 'active' : ''}`}
            onClick={() => goToSlide(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroBanner;
